<template>
    <div class="container form-container p-4">
        <div class="text-3xl mb-4 pb-2 font-light flex items-center justify-between">
            <div data-cy="covers-title">{{ $t('admin.covers.title') }}</div>
            <div class="flex flex-col">
                <a-dropdown :trigger="['click']" class="shadow-none" data-cy="cover-import-export-dropdown">
                    <a-button style="margin-left: 8px">
                        {{ $t('covers.import-export.title') }}
                        <DownOutlined />
                    </a-button>
                    <template #overlay>
                        <a-menu @click="onSelectClick">
                            <a-menu-item data-cy="cover-import-export-select" key="CoverRemoval">
                                <ExceptionOutlined />
                                {{ $t('covers.import-export.removal') }}
                            </a-menu-item>
                        </a-menu>
                    </template>
                </a-dropdown>
            </div>
        </div>
        <div class="flex flex-col">
            <div class="flex justify-between" data-cy="cover-search">
                <div class="flex flex-col" data-cy="field-basic-search">
                    <div class="flex">
                        <a-input-search
                            @search="handleFind"
                            v-model:value="search_form.basic_search"
                            :placeholder="$t('globals.tables.search')"
                        />
                        <a-button
                            v-if="covers_search_criteria"
                            @click="handleReload"
                            class="ml-2"
                            data-cy="clear-cover-document-btn"
                        >
                            {{ $t('covers.search_form.buttons.clear') }}
                        </a-button>
                    </div>
                </div>
                <div>
                    <a-button
                        data-cy="advance-search-show-button"
                        type="primary"
                        @click="handleSwitch"
                        v-if="!is_advance_search_visible"
                    >
                        {{ $t('covers.table.actions.show_advance_search') }}
                    </a-button>
                    <a-button
                        data-cy="advance-search-hide-button"
                        type="primary"
                        @click="handleSwitch"
                        v-if="is_advance_search_visible"
                    >
                        {{ $t('covers.table.actions.hide_advance_search') }}
                    </a-button>
                </div>
            </div>
            <template v-if="is_advance_search_visible">
                <form-cover-admin-search
                    data-cy="cover-advance-search"
                    :processing="processing"
                    :search_form="search_form"
                    @formSubmit="handleFind"
                    @clear="
                        handleClear();
                        handleReload();
                    "
                    @cancel="handleCancel"
                    :section_type="section_type"
                />
            </template>
            <div class="list-table overflow-x-auto overflow-visible" :class="{ 'pt-2 md:pt-4': !isCiaEnabled }">
                <covers-table
                    :search_id="search_id"
                    @onTableAction="onTableAction"
                    :search_criteria="covers_search_criteria"
                    @fetchSearchCriteria="handleFetchData"
                    @fetchSearchCount="handleFetchCount"
                    @onCoverClick="onCoverClick"
                    :section_type="isCiaEnabled ? 'cia' : 'cover'"
                    :is_settings="true"
                    :setting_section="section_type"
                    :association_id="association_id"
                    :company_id="company_id"
                    ref="CoversTable"
                />
            </div>
        </div>
        <a-drawer
            data-cy="cover-drawer"
            :title="'Cover Details ' + '[Cover ID: ' + cia_cover.cover_id + ']'"
            :width="800"
            :open="drawer_visible"
            :body-style="{ padding: '0px', paddingBottom: '10px' }"
            :destroyOnClose="true"
            :maskClosable="false"
            :rootClassName="'epic-' + moduleStateLink"
            @close="onClose"
        >
            <form-cover
                v-if="isCiaEnabled"
                data-cy="cover-form"
                :cover_id="cover_id"
                :cover="cia_cover"
                :validation="validation"
                :is_view="cia_cover.print_count > 0 || cia_cover.cover_status != 'active'"
                @is-form-dirty="isFormDirty"
                @formSubmit="onSave"
                @update="updateCover"
                @cancel="onCancel"
                @print="onPrint"
                :processing="processing"
                :display_type="'drawer'"
                :form_mode="cover_id != '' ? 'edit' : 'init'"
                ref="CoversForm"
            />
            <div class="cover-details p-4" v-if="!isCiaEnabled">
                <view-cover class="cover-view-form" data-cy="cover-view" :is_settings="true" :cover="cover" />
                <div
                    class="header-text-vehicle w-full text-sm text-default-light-gray ml-2 font-bold"
                    data-cy="vehicle-detail-header-vehicle"
                >
                    {{ $t('covers.view.headers.vehicles') }}
                </div>
                <vehicle-table class="pb-8" data-cy="vehicle-table" :table_data="vehicle_data" />
                <div class="flex flex-row justify-end">
                    <a-button
                        v-if="isClaimsVaultEnabled"
                        class="m-2"
                        data-cy="check-claim-button"
                        @click="showClaimsModal"
                        type="primary"
                    >
                        {{ $t('covers.view.buttons.claim') }} : {{ cover.claims_history }}
                    </a-button>
                    <a-button class="m-2" type="primary" @click="onCancel" data-cy="button-return-to-result">
                        {{ $t('covers.view.buttons.return-result') }}
                    </a-button>
                </div>
            </div>
        </a-drawer>
        <modal-remove-cover-data @formSubmit="onRemoval" :processing="processing" ref="RemoveDataModal" />
        <modal-claims :cover_id="cover_id" ref="ClaimsModal" />
        <modal-cascade @formSubmit="handleCascadeSubmit" :cover="cascade_cover" ref="CascadeModal" />
    </div>
</template>

<script>
import CoversTable from '@/components/tables/Covers.vue';
import FormCoverAdminSearch from '@/components/forms/covers/AdminSearch.vue';
import FormCover from '@/components/forms/Cover.vue';
import ViewCover from '@/components/views/Cover.vue';
import VehicleTable from '@/components/tables/CoverVehicle.vue';
import ModalRemoveCoverData from '@/components/modals/RemoveCoverData.vue';
import ModalClaims from '@/components/modals/Claims.vue';
import ModalCascade from '@/components/modals/Cascade.vue';

import store from '@/store';
import { DownOutlined, ExceptionOutlined } from '@ant-design/icons-vue';

export default {
    components: {
        CoversTable,
        FormCoverAdminSearch,
        FormCover,
        ViewCover,
        VehicleTable,
        ModalRemoveCoverData,
        ModalClaims,
        ModalCascade,
        DownOutlined,
        ExceptionOutlined,
    },
    props: {
        association_id: {
            type: String,
            default: '',
        },
        company_id: {
            type: String,
            default: '',
        },
    },
    data() {
        return {
            search_id: '',
            search_form: {
                basic_search: '',
                cover_id: '',
                cover_type: '',
                reg_no: '',
                years_from: 'all',
                chassis_no: '',
                engine_no: '',
                association_id: '',
                insurer_policy_number: '',
                insurer_id: '',
                intermediary_id: '',
                cover_expiry_date: {
                    start_date: null,
                    end_date: null,
                },
                imported_date: {
                    start_date: null,
                    end_date: null,
                },
                all_match: 1,
                file_source: '',
            },
            form_mode: 'results',
            search_values: {},
            covers_search_count: 0,
            covers_search_criteria: {},
            processing: false,
            drawer_visible: false,
            cover: {},
            vehicle_data: [],
            cia_cover: {
                insurer_id: '',
                company_id: '',
                policy_number: '',
                product_code: '',
                insurer_policy_number: '',
                broker_policy_number: '',
                policy_start_date: null,
                policy_end_date: null,
                policy_date_range: {
                    start_date: null,
                    end_date: null,
                },
                cover_effective_date: null,
                cover_expiry_date: null,
                cover_date_range: {
                    start_date: null,
                    end_date: null,
                },
                certificate_type: '',
                cover_type: '',
                cover_status: 'active',
                is_motor_trade: false,
                manual_certificate_number: '',
                policyholder_address: '',
                risk_mortgagee: '',
                cover_category: '',
                section_1_label: '',
                section_1: '',
                section_2_label: '',
                section_2: '',
                section_3_label: '',
                section_3: '',
                section_4_label: '',
                section_4: '',
                registration_number: '',
                chassis_number: '',
                vehicle_make: '',
                vehicle_model: '',
                vehicle_year: null,
                vehicle_color: '',
                engine_number: '',
                sum_insured: 0,
                hpcc: '',
                seating: '',
                is_authorized: false,
                can_authorize: false,
            },
            cascade_cover: {
                insurer_id: '',
                company_id: '',
                policy_number: '',
                product_code: '',
                insurer_policy_number: '',
                broker_policy_number: '',
                policy_start_date: null,
                policy_end_date: null,
                policy_date_range: {
                    start_date: null,
                    end_date: null,
                },
                cover_effective_date: null,
                cover_expiry_date: null,
                cover_date_range: {
                    start_date: null,
                    end_date: null,
                },
                certificate_code: '',
                cover_type: '',
                cover_status: 'active',
                manual_certificate_number: '',
                policyholder_address: '',
                risk_mortgagee: '',
                cover_category: '',
                section_1_label: '',
                section_1: '',
                section_2_label: '',
                section_2: '',
                section_3_label: '',
                section_3: '',
                section_4_label: '',
                section_4: '',
                registration_number: '',
                chassis_number: '',
                vehicle_make: '',
                vehicle_model: '',
                vehicle_year: null,
                vehicle_color: '',
                engine_number: '',
                sum_insured: 0,
                hpcc: '',
                seating: '',
            },
            cascade_cover_id: '',
            form_dirty: false,
            validation: {
                errors: {},
                message: '',
            },
            cover_id: '',
            is_advance_search_visible: false,
        };
    },
    mounted() {
        if (this.isCiaEnabled) {
            this.$refs.CoversTable.fetch({}, this.search_id);
        }
    },
    computed: {
        section_type() {
            if (
                (this.association_id === '' || this.association_id === undefined || this.association_id === null) &&
                (this.company_id === '' || this.company_id === undefined || this.company_id === null)
            ) {
                return 'site_admin';
            } else if (this.company_id === '' || this.company_id === undefined || this.company_id === null) {
                return 'association';
            } else {
                return 'company';
            }
        },
        switchButtonText() {
            switch (this.$store.state.system.search_state_covers) {
                case 'basic':
                    return this.$t('covers.search_form.buttons.switch_basic');
                case 'advance':
                    return this.$t('covers.search_form.buttons.switch_advance');
            }
        },
        isCiaEnabled() {
            return this.$store.state.user.modules.cia;
        },
        isClaimsVaultEnabled() {
            return this.$store.state.user.modules.claimsvault;
        },
        moduleStateLink() {
            return this.$store.state.system.is_epicvault_enabled ? 'default' : 'cover';
        },
    },
    methods: {
        handleFind() {
            this.search_values = this.search_form;
            let years_from = 0;

            if (this.search_values.years_from !== 'all') {
                years_from = parseInt(this.search_values.years_from) || 0;
            }

            this.search_values.cover_expiry_start_date =
                this.search_form.cover_expiry_date.start_date != null
                    ? this.search_form.cover_expiry_date.start_date.format('YYYY-MM-DD')
                    : null;

            this.search_values.cover_expiry_end_date =
                this.search_form.cover_expiry_date.end_date != null
                    ? this.search_form.cover_expiry_date.end_date.format('YYYY-MM-DD')
                    : null;

            this.search_values.imported_start_date =
                this.search_form.imported_date.start_date != null
                    ? this.search_form.imported_date.start_date.format('YYYY-MM-DD')
                    : null;

            this.search_values.imported_end_date =
                this.search_form.imported_date.end_date != null
                    ? this.search_form.imported_date.end_date.format('YYYY-MM-DD')
                    : null;

            if (this.is_advance_search_visible) {
                if (
                    this.search_values.cover_id == '' &&
                    this.search_values.cover_type == '' &&
                    this.search_values.reg_no == '' &&
                    this.search_values.chassis_no == '' &&
                    this.search_values.engine_no == '' &&
                    this.search_values.association_id == '' &&
                    this.search_values.insurer_policy_number == '' &&
                    this.search_values.insurer_id == '' &&
                    this.search_values.intermediary_id == '' &&
                    this.search_values.cover_expiry_start_date == null &&
                    this.search_values.cover_expiry_end_date == null &&
                    this.search_values.imported_start_date == null &&
                    this.search_values.imported_end_date == null &&
                    this.search_values.file_source == ''
                ) {
                    this.$notification.error({
                        message: this.$t('covers.notifications.error.advance_empty'),
                    });

                    return;
                }

                if (
                    (this.search_values.cover_id != '' && this.search_values.cover_type == '') ||
                    (this.search_values.cover_id == '' && this.search_values.cover_type != '')
                ) {
                    this.$notification.error({
                        message: this.$t('covers.notifications.error.coverid_or_covertype_required'),
                    });
                    return;
                }
            } else {
                if (this.isCiaEnabled) {
                    if (this.search_values.basic_search == '') {
                        this.$notification.error({
                            message: this.$t('covers.notifications.error.basic_empty'),
                        });
                        return;
                    }
                    this.search_values.reg_no = this.search_values.basic_search;
                    this.search_values.chassis_no = this.search_values.basic_search;
                    this.search_values.insurer_policy_number = this.search_values.basic_search;
                    this.search_values.broker_policy_number = this.search_values.basic_search;

                    this.search_values = {
                        ...this.search_values,
                        basic_search: '',
                        all_match: false,
                    };

                    years_from = 'all';
                } else if (this.search_values.basic_search == '') {
                    this.$notification.error({
                        message: this.$t('covers.notifications.error.basic_empty'),
                    });
                    return;
                }
            }

            this.processSearchValues({
                ...this.search_values,
                cover_expiry_date: null,
                imported_date: null,
                years_from: years_from,
            });
        },
        handleClear() {
            this.processing = true;

            this.search_form = {
                basic_search: '',
                cover_id: '',
                cover_type: '',
                reg_no: '',
                years_from: 'all',
                chassis_no: '',
                engine_no: '',
                association_id: '',
                insurer_policy_number: '',
                insurer_id: '',
                intermediary_id: '',
                cover_expiry_date: {
                    start_date: null,
                    end_date: null,
                },
                imported_date: {
                    start_date: null,
                    end_date: null,
                },
                all_match: 1,
                file_source: '',
            };

            this.processing = false;
        },
        onCoverClick(cover_id) {
            this.cover_id = cover_id;

            if (this.processing) {
                return false;
            }

            this.$http
                .get('/api/covers/' + cover_id)
                .then((response) => {
                    let coverData = {
                        ...response.data.data,
                    };
                    this.cover = coverData;
                    this.vehicle_data = [this.cover];
                    this.drawer_visible = true;
                })
                .catch((error) => {
                    this.$notification.error({
                        message: this.$t('globals.global.notifications.error.any', { code: 'CVCVIEW10' }),
                    });
                })
                .finally(() => {
                    this.processing = false;
                });
        },
        showClaimsModal() {
            if (this.isClaimsVaultEnabled) {
                this.$refs.ClaimsModal.showModal();
            }
        },
        handleCancel() {
            store.dispatch('system/updateSearchStateCovers', 'basic');
            this.handleClear();
        },
        handleFetchData(values) {
            if (this.processing) {
                return false;
            }

            this.covers_search_criteria = values.search_criteria;
            this.processing = false;
        },
        handleFetchCount(values) {
            this.covers_search_count = values.search_count;
        },
        handleReload() {
            this.$refs.CoversTable.clear();
        },
        processSearchValues(data) {
            this.processing = true;

            if (this.$store.getters['user/isMultiAssociation']) {
                data.association_id = this.$store.getters['user/currentAssociationId'];
            }

            this.$http
                .post('/api/covers/search', data)
                .then((response) => {
                    if (response.data.status == 'success') {
                        this.search_id = response.data.id;
                        this.$refs.CoversTable.selected_row_keys = [];
                        this.$refs.CoversTable.fetch({}, this.search_id);
                    } else {
                        this.$notification.error({
                            message: response.data.message,
                        });
                    }
                })
                .catch((error) => {
                    if (error.response.status == 403) {
                        this.$notification.error({
                            message: this.$t('globals.global.notifications.error.403_mod'),
                        });
                    } else {
                        this.$notification.error({
                            message: this.$t('globals.global.notifications.error.any', { code: 'CVSB01' }),
                        });
                    }
                })
                .finally(() => {
                    this.processing = false;
                });
        },
        handleSwitch() {
            this.is_advance_search_visible = !this.is_advance_search_visible;
            this.handleClear();
        },
        onTableAction(data) {
            switch (data.key) {
                case 'edit':
                    this.onDrawerOpen(data.id);
                    break;
                case 'cascade':
                    this.onCascadeCover(data.id);
                    break;
                case 'delete':
                    break;
                case 'print':
                    break;
            }
        },
        isFormDirty(is_dirty) {
            this.form_dirty = is_dirty;
        },
        onDrawerOpen(id) {
            this.cover_id = id;
            this.fetch();
        },
        onCascadeCover(id) {
            this.cascade_cover_id = id;

            this.$http
                .get('/api/covers/' + id)
                .then((response) => {
                    this.cascade_cover = {
                        ...this.cascade_cover,
                        ...response.data.data,
                        id: null,
                        insurer_id: response.data.data.company_id,
                        insurer_name: response.data.data.name,
                        company_id: response.data.data.intermediary_id,
                        policy_date_range: {
                            start_date: this.$dayjs(response.data.data.policy_start_date),
                            end_date: this.$dayjs(response.data.data.policy_end_date),
                        },
                        cover_date_range: {
                            start_date: this.$dayjs(response.data.data.cover_effective_date),
                            end_date: this.$dayjs(response.data.data.cover_expiry_date),
                        },
                        printed_first_at:
                            response.data.data.printed_first_at == null
                                ? 'N/A'
                                : this.$dayjs(response.data.data.printed_first_at).format('YYYY-MM-DD HH:mm:ss'),
                        printed_last_at:
                            response.data.data.printed_last_at == null
                                ? 'N/A'
                                : this.$dayjs(response.data.data.printed_last_at).format('YYYY-MM-DD HH:mm:ss'),
                        printed_last_by:
                            response.data.data.printed_last_by == null ? 'N/A' : response.data.data.printed_last_by,
                        created_by: response.data.data.created_by == null ? 'N/A' : response.data.data.created_by,
                        created_at:
                            response.data.data.created_at == null
                                ? 'N/A'
                                : this.$dayjs(response.data.data.created_at).format('YYYY-MM-DD HH:mm:ss'),
                        last_updated_by:
                            response.data.data.last_updated_by == null ? 'N/A' : response.data.data.last_updated_by,
                        updated_at:
                            response.data.data.updated_at == null
                                ? 'N/A'
                                : this.$dayjs(response.data.data.updated_at).format('YYYY-MM-DD HH:mm:ss'),
                    };
                    this.$refs.CascadeModal.showModal();
                })
                .catch((error) => {
                    this.$notification.error({
                        message: response.data.message,
                    });
                })
                .finally(() => {
                    this.processing = false;
                });
        },
        handleCascadeSubmit(data) {
            let submit_data = {
                keys: [],
            };

            Object.keys(data).forEach((key, index) => {
                if (data[key]) {
                    submit_data.keys.push(key);
                }
            });

            this.processing = true;

            this.$http
                .post('/api/covers/cascade/' + this.cascade_cover_id, submit_data)
                .then((response) => {
                    if (response.data.status == 'success') {
                        this.$notification.success({
                            message: response.data.message,
                        });
                        this.$refs.CoversTable.reload();
                    } else {
                        this.$notification.error({
                            message: response.data.message,
                        });
                    }
                })
                .catch((error) => {
                    if (error.response.status == 403) {
                        this.$notification.error({
                            message: this.$t('globals.global.notifications.error.403_mod'),
                        });
                    } else {
                        this.$notification.error({
                            message: this.$t('globals.global.notifications.error.any', { code: 'CVCS05' }),
                        });
                    }
                })
                .finally(() => {
                    this.processing = false;
                });
        },
        onClose() {
            this.drawer_visible = false;
        },
        onSave() {
            if (this.processing) {
                return false;
            }

            this.form_dirty = false;
            this.processing = true;

            let id = this.cia_cover.id;

            let params = {
                ...this.cia_cover,
                company_id: this.cia_cover.insurer_id,
                intermediary_id: this.cia_cover.company_id,
                policy_name_list: this.$refs.CoversForm.policy_name_list,
                cover_date_range: null,
                policy_date_range: null,
                policy_start_date: this.cover_data.policy_date_range.start_date.format('YYYY-MM-DD'),
                policy_end_date: this.cover_data.policy_date_range.end_date.format('YYYY-MM-DD'),
                cover_effective_date: this.cover_data.cover_date_range.start_date.format('YYYY-MM-DD'),
                cover_expiry_date: this.cover_data.cover_date_range.end_date.format('YYYY-MM-DD'),
                policy_start_time: this.cover_data.policy_date_range.start_date.format('HH:mm:ss'),
                policy_end_time: this.cover_data.policy_date_range.end_date.format('HH:mm:ss'),
                cover_effective_time: this.cover_data.cover_date_range.start_date.format('HH:mm:ss'),
                cover_expiry_time: this.cover_data.cover_date_range.end_date.format('HH:mm:ss'),
            };

            this.$http
                .post('/api/covers/update/' + this.cover_id, params)
                .then((response) => {
                    if (response.data.status == 'success') {
                        this.form_dirty = false;
                        this.$notification.success({
                            message: response.data.message,
                        });
                        this.drawer_visible = false;
                    } else {
                        this.$notification.error({
                            message: response.data.message,
                        });
                    }
                })
                .catch((error) => {
                    if (error.response.status == 422) {
                        this.validation = error.response.data;
                    } else if (error.response.status == 403) {
                        this.$notification.error({
                            message: this.$t('globals.global.notifications.error.403_mod'),
                        });
                    } else {
                        this.$notification.error({
                            message: this.$t('globals.global.notifications.error.any', { code: 'CVCA0136' }),
                        });
                    }
                })
                .finally(() => {
                    this.processing = false;
                });
        },
        updateCover(value) {
            this.cia_cover = {
                ...this.cia_cover,
                ...value,
            };
        },
        onCancel() {
            this.drawer_visible = false;
        },
        fetch() {
            const id = this.cover_id;
            this.processing = true;

            this.$http
                .get('/api/covers/' + id)
                .then((response) => {
                    if (response.data.status == 'error') {
                        this.$notification.error({
                            message: response.data.message,
                        });
                    } else {
                        this.cia_cover = {
                            ...this.cia_cover,
                            ...response.data.data,
                            id: null,
                            insurer_id: response.data.data.company_id,
                            insurer_name: response.data.data.name,
                            company_id: response.data.data.intermediary_id,
                            policy_date_range: {
                                start_date: this.$dayjs(response.data.data.policy_start_date),
                                end_date: this.$dayjs(response.data.data.policy_end_date),
                            },
                            is_motor_trade: response.data.data.is_motor_trade === 1 ? true : false,
                            cover_date_range: {
                                start_date: this.$dayjs(response.data.data.cover_effective_date),
                                end_date: this.$dayjs(response.data.data.cover_expiry_date),
                            },
                            printed_first_at:
                                response.data.data.printed_first_at == null
                                    ? 'N/A'
                                    : this.$dayjs(response.data.data.printed_first_at).format('YYYY-MM-DD HH:mm:ss'),
                            printed_last_at:
                                response.data.data.printed_last_at == null
                                    ? 'N/A'
                                    : this.$dayjs(response.data.data.printed_last_at).format('YYYY-MM-DD HH:mm:ss'),
                            printed_last_by:
                                response.data.data.printed_last_by == null ? 'N/A' : response.data.data.printed_last_by,
                            created_by: response.data.data.created_by == null ? 'N/A' : response.data.data.created_by,
                            created_at:
                                response.data.data.created_at == null
                                    ? 'N/A'
                                    : this.$dayjs(response.data.data.created_at).format('YYYY-MM-DD HH:mm:ss'),
                            last_updated_by:
                                response.data.data.last_updated_by == null ? 'N/A' : response.data.data.last_updated_by,
                            updated_at:
                                response.data.data.updated_at == null
                                    ? 'N/A'
                                    : this.$dayjs(response.data.data.updated_at).format('YYYY-MM-DD HH:mm:ss'),
                            is_authorized: response.data.data.is_authorized === 1 ? true : false,
                            can_authorize: response.data.data.can_authorize === 1 ? true : false,
                        };

                        this.drawer_visible = true;
                    }
                })
                .catch((error) => {
                    if (error.response.status == 403) {
                        this.$notification.error({
                            message: this.$t('globals.global.notifications.error.403_mod'),
                        });
                    } else {
                        this.$notification.error({
                            message: this.$t('globals.global.notifications.error.any', { code: 'CVCE02' }),
                        });
                    }
                })
                .finally(() => {
                    this.processing = false;

                    if (this.cia_cover.cover_effective_date !== null && this.cia_cover.cover_expiry_date !== null) {
                        this.$refs.CoversForm.dateDifference({
                            start_date: this.cia_cover.cover_effective_date,
                            end_date: this.cia_cover.cover_expiry_date,
                        });
                    }
                });
        },
        onPrint() {
            this.fetch();
        },
        onSelectClick(event) {
            switch (event.key) {
                case 'CoverRemoval':
                    this.$refs.RemoveDataModal.showModal();
                    break;
            }
        },
        onRemoval(data) {
            if (this.processing) {
                return false;
            }

            this.processing = true;
            const headers = { 'Content-Type': 'multipart/form-data' };

            this.$http
                .post('/api/removal/covers', data, { headers })
                .then((response) => {
                    if (response.data.status == 'success') {
                        this.$refs.RemoveDataModal.modal_visible = false;
                        this.reload();

                        this.$notification.success({ message: response.data.message });
                    } else {
                        this.$notification.error({ message: response.data.message });
                    }
                })
                .catch((error) => {
                    if (error.response.status == 422) {
                        this.validation = error.response.data;
                    } else if (error.response.status == 403) {
                        this.$notification.error({
                            message: this.$t('globals.global.notifications.error.403_mod'),
                        });
                    } else {
                        this.$notification.error({
                            message: this.$t('globals.global.notifications.error.any', { code: 'CVCRD02' }),
                        });
                    }
                })
                .finally(() => {
                    this.processing = false;
                });
        },
        reload() {
            this.$refs.CoversTable.reload();
        },
    },
};
</script>
